import React, { useState, useEffect } from 'react';
import { IcPlus, IcPencil, IcTrash, IcChevronDown, IcChevronUp, IcClose, IcSearch, IcPackageOpen, IcTags, IcNetwork, IcLayers } from '../../theme/icons';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import productService from '../../services/productService';
import SectionCard from '../../components/SectionCard';
import { CardSkeleton, TableSkeleton } from '../../components/Skeleton';
import { matchesSearch } from '../../utils/search';

// `embedded` : rendu dans Paramètres › Catégories produits (entête compact, sans chip).
const CategoryList = ({ embedded = false } = {}) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ id: null, name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);
  
  // State for subcategory form
  const [subcategoryFormData, setSubcategoryFormData] = useState({ 
    id: null, 
    name: '', 
    description: '',
    category_id: null 
  });
  const [isEditingSubcategory, setIsEditingSubcategory] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  
  // Track expanded categories (to show subcategories)
  const [expandedCategories, setExpandedCategories] = useState({});
  // Refonte 2026-09-08 (owner) : le formulaire catégorie vit dans une modale
  // (même patron que la sous-catégorie) ; recherche nom / description / sous-cat.
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  // Paramètres : catégorie sélectionnée dans le volet gauche (2 volets).
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchAllSubcategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAllCategories();
      // Handle both response structures: nested results or direct array
      const categoryData = response.results || response || [];
      console.log('Categories fetched:', categoryData);
      setCategories(categoryData);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again later.');
      setLoading(false);
    }
  };

  const fetchAllSubcategories = async () => {
    try {
      const response = await subcategoryService.getAllSubcategories();
      const subcategoryData = response.results || response || [];
      setSubcategories(subcategoryData);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setError('Failed to load subcategories. Please try again later.');
    }
  };
  
  const fetchProducts = async () => {
    try {
      const response = await productService.getAllProducts();
      const productData = response || [];
      setProducts(productData);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  
  const fetchSubcategoriesForCategory = async (categoryId) => {
    try {
      const response = await subcategoryService.getSubcategoriesByCategory(categoryId);
      const subcategoryData = response.results || response || [];
      return subcategoryData;
    } catch (err) {
      console.error(`Error fetching subcategories for category ${categoryId}:`, err);
      return [];
    }
  };
  
  const toggleCategoryExpansion = async (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Le nom de la catégorie est requis.');
      return;
    }

    try {
      if (isEditing) {
        // Update existing category
        await categoryService.updateCategory(formData.id, formData);
      } else {
        // Create new category
        await categoryService.createCategory(formData);
      }
      
      // Reset form and refresh categories
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
      setShowCategoryForm(false);
      fetchCategories();
      
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde de la catégorie.');
    }
  };

  const handleEdit = (category) => {
    setFormData({
      id: category.id,
      name: category.name,
      description: category.description || ''
    });
    setIsEditing(true);
    setShowCategoryForm(true);
  };

  const handleNewCategory = () => {
    setFormData({ id: null, name: '', description: '' });
    setIsEditing(false);
    setShowCategoryForm(true);
  };

  const handleCancel = () => {
    setFormData({ id: null, name: '', description: '' });
    setIsEditing(false);
    setShowCategoryForm(false);
  };

  const handleSubcategoryChange = (e) => {
    const { name, value } = e.target;
    setSubcategoryFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleAddSubcategoryClick = (categoryId, categoryName) => {
    setSubcategoryFormData({
      id: null,
      name: '',
      description: '',
      category_id: categoryId
    });
    setIsEditingSubcategory(false);
    setShowSubcategoryForm(true);
  };

  const handleEditSubcategory = (subcategory) => {
    setSubcategoryFormData({
      id: subcategory.id,
      name: subcategory.name,
      description: subcategory.description || '',
      category_id: subcategory.category_id
    });
    setIsEditingSubcategory(true);
    setShowSubcategoryForm(true);
  };

  const handleCancelSubcategory = () => {
    setSubcategoryFormData({ id: null, name: '', description: '', category_id: null });
    setIsEditingSubcategory(false);
    setShowSubcategoryForm(false);
  };

  const handleSubmitSubcategory = async (e) => {
    e.preventDefault();
    
    if (!subcategoryFormData.name.trim()) {
      setError('Le nom de la sous-catégorie est requis.');
      return;
    }

    if (!subcategoryFormData.category_id) {
      setError('Veuillez sélectionner une catégorie parent.');
      return;
    }

    try {
      if (isEditingSubcategory) {
        // Update existing subcategory
        await subcategoryService.updateSubcategory(subcategoryFormData.id, subcategoryFormData);
      } else {
        // Create new subcategory
        await subcategoryService.createSubcategory(subcategoryFormData);
      }
      
      // Reset form and refresh subcategories
      setSubcategoryFormData({ id: null, name: '', description: '', category_id: null });
      setIsEditingSubcategory(false);
      setShowSubcategoryForm(false);
      fetchAllSubcategories();
      
    } catch (err) {
      console.error('Error saving subcategory:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde de la sous-catégorie.');
    }
  };

  // Helper functions to count products
  const getProductCountForCategory = (categoryId) => {
    return products.filter(p => p.category && (p.category === categoryId || p.category.id === categoryId)).length;
  };
  
  const getProductCountForSubcategory = (subcategoryId) => {
    return products.filter(p => p.subcategory && (p.subcategory === subcategoryId || p.subcategory.id === subcategoryId)).length;
  };
  
  const getSubcategoryCountForCategory = (categoryId) => {
    return subcategories.filter(s => s.category_id === categoryId).length;
  };

  const handleDeleteSubcategory = async (subcategory) => {
    const productCount = getProductCountForSubcategory(subcategory.id);
    
    let confirmMessage = `⚠️ ATTENTION ⚠️\n\nÊtes-vous sûr de vouloir supprimer la sous-catégorie "${subcategory.name}" ?`;
    
    if (productCount > 0) {
      confirmMessage += `\n\n📦 Cette sous-catégorie contient ${productCount} produit(s).`;
      confirmMessage += `\n\n❌ Les produits associés perdront leur lien avec cette sous-catégorie.`;
    }
    
    confirmMessage += `\n\nCette action est IRRÉVERSIBLE !`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await subcategoryService.deleteSubcategory(subcategory.id);
        fetchAllSubcategories();
        fetchProducts(); // Refresh to update counts
      } catch (err) {
        console.error('Error deleting subcategory:', err);
        alert('❌ Échec de la suppression de la sous-catégorie. Veuillez réessayer.');
      }
    }
  };

  const handleDeleteCategory = async (category) => {
    const productCount = getProductCountForCategory(category.id);
    const subcategoryCount = getSubcategoryCountForCategory(category.id);
    
    let confirmMessage = `⚠️ ATTENTION - SUPPRESSION CRITIQUE ⚠️\n\nÊtes-vous sûr de vouloir supprimer la catégorie "${category.name}" ?`;
    
    if (subcategoryCount > 0) {
      confirmMessage += `\n\n🗂️ Cette catégorie contient ${subcategoryCount} sous-catégorie(s).`;
      confirmMessage += `\n❌ TOUTES les sous-catégories seront SUPPRIMÉES !`;
    }
    
    if (productCount > 0) {
      confirmMessage += `\n\n📦 Cette catégorie contient ${productCount} produit(s).`;
      confirmMessage += `\n❌ Les produits associés perdront leur lien avec cette catégorie.`;
    }
    
    if (subcategoryCount > 0 || productCount > 0) {
      confirmMessage += `\n\n💥 IMPACT TOTAL :`;
      if (subcategoryCount > 0) confirmMessage += `\n  • ${subcategoryCount} sous-catégorie(s) supprimée(s)`;
      if (productCount > 0) confirmMessage += `\n  • ${productCount} produit(s) affecté(s)`;
    }
    
    confirmMessage += `\n\n⚠️ Cette action est IRRÉVERSIBLE !`;
    confirmMessage += `\n\nTapez "SUPPRIMER" pour confirmer (en majuscules)`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput === 'SUPPRIMER') {
      try {
        await categoryService.deleteCategory(category.id);
        fetchCategories();
        fetchAllSubcategories();
        fetchProducts(); // Refresh to update counts
      } catch (err) {
        console.error('Error deleting category:', err);
        alert('❌ Échec de la suppression de la catégorie. Veuillez réessayer.');
      }
    } else if (userInput !== null) {
      alert('❌ Suppression annulée : confirmation incorrecte.');
    }
  };

  // ---- Recherche : catégorie (nom / description) ou une de ses sous-catégories ----
  // Règle Ki Para (utils/search.js) : début de mot, sans accents.
  const q = search.trim();
  const matches = (txt) => matchesSearch(q, txt);
  const visibleCategories = q
    ? categories.filter((c) =>
        matches(c.name) || matches(c.description) ||
        subcategories.some((sub) => sub.category_id === c.id && (matches(sub.name) || matches(sub.description)))
      )
    : categories;
  const visibleSubs = (categoryId) => {
    const subs = subcategories.filter((sub) => sub.category_id === categoryId);
    if (!q) return subs;
    const parent = categories.find((c) => c.id === categoryId);
    if (parent && (matches(parent.name) || matches(parent.description))) return subs;
    return subs.filter((sub) => matches(sub.name) || matches(sub.description));
  };
  // Une recherche déplie les catégories concernées ; sinon on respecte le clic.
  const isExpanded = (categoryId) => (q ? true : !!expandedCategories[categoryId]);
  const allExpanded = categories.length > 0 && categories.every((c) => expandedCategories[c.id]);
  const toggleAll = () =>
    setExpandedCategories(allExpanded ? {} : Object.fromEntries(categories.map((c) => [c.id, true])));
  const classifiedProducts = products.filter((p) => p.category).length;

  const listContent = loading ? (
        <TableSkeleton rows={6} columns={[
              { label: 'Catégorie', w: 300 },
              { label: 'Sous-catégories', w: 80 },
              { label: 'Produits', w: 80 },
              { label: 'Actions', align: 'right', w: 60 },
            ]} />
      ) : visibleCategories.length === 0 ? (
        /* État vide (Composants-2 §9) : cadre pointillé, icône, phrase 600,
           sous-phrase, action encre. */
        <div className={`empty-ki rounded-2xl p-12 ${embedded ? 'm-6' : ''}`}>
          <IcTags size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">
            {q ? 'Aucune catégorie correspondant à la recherche' : 'Aucune catégorie trouvée'}
          </p>
          <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">
            {q
              ? 'Essayez un autre mot, ou effacez la recherche.'
              : 'Pour commencer, créez votre première catégorie de produits.'}
          </p>
          {!q && (
            <button type="button" onClick={handleNewCategory} className="btn-add-block w-auto mt-4 px-4 py-2.5">
              <IcPlus className="mr-2" /> Ajouter une catégorie
            </button>
          )}
        </div>
      ) : (
        <div className={embedded ? '' : 'bg-white rounded-2xl border border-gray-200 overflow-hidden'}>
          {/* Entête de carte (page seule) : titre + compte, « Tout déplier / replier » à droite */}
          {!embedded && (
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">Liste des catégories</h2>
              <p className="text-xs text-gray-400 tabular-nums">
                {q
                  ? `${visibleCategories.length} sur ${categories.length} catégorie(s)`
                  : `${categories.length} catégorie(s) · ${subcategories.length} sous-catégorie(s)`}
              </p>
            </div>
            {!q && (
              <button type="button" onClick={toggleAll} className="btn-s px-3 py-1.5 rounded-lg text-xs shrink-0">
                {allExpanded ? <IcChevronUp size={12} /> : <IcChevronDown size={12} />}
                {allExpanded ? 'Tout replier' : 'Tout déplier'}
              </button>
            )}
          </div>
          )}

          <div className="overflow-x-auto">
            {/* Tableau dense (Composants-2 §6) : en-tête th-ki, nom 600,
                description sous le nom, survol #F0FDF4. */}
            <table className="min-w-full">
              <thead>
                <tr>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase">Catégorie</th>
                  <th scope="col" className="th-ki px-5 py-3 text-center uppercase whitespace-nowrap w-44">Sous-catégories</th>
                  <th scope="col" className="th-ki px-5 py-3 text-center uppercase w-36">Produits</th>
                  <th scope="col" className="th-ki px-5 py-3 text-right uppercase w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategories.map((category) => {
                  const subs = visibleSubs(category.id);
                  const subCount = getSubcategoryCountForCategory(category.id);
                  const open = isExpanded(category.id);
                  return (
                    <React.Fragment key={category.id}>
                      <tr className="border-t border-gray-100 hover:bg-gray-100 transition-colors">
                        <td className="td-ki px-5 py-3.5 text-sm">
                          <div className="flex items-start gap-2 min-w-0">
                            <button
                              type="button"
                              onClick={() => toggleCategoryExpansion(category.id)}
                              disabled={!!q}
                              aria-expanded={open}
                              aria-label={open ? 'Replier' : 'Déplier'}
                              className="h-7 w-7 shrink-0 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                              {open ? <IcChevronUp size={12} /> : <IcChevronDown size={12} />}
                            </button>
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => toggleCategoryExpansion(category.id)}
                                className="font-semibold text-gray-900 text-left leading-tight hover:underline underline-offset-2"
                              >
                                {category.name}
                              </button>
                              {category.description && (
                                <p className="text-xs text-gray-400 mt-0.5 max-w-md truncate" title={category.description}>
                                  {category.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="td-ki px-5 py-3.5 text-sm whitespace-nowrap text-center">
                          <span className="bdg bdg-neutral">
                            <IcNetwork size={10} />
                            {subCount}
                          </span>
                        </td>
                        <td className="td-ki px-5 py-3.5 text-sm whitespace-nowrap text-center">
                          <span className="bdg bdg-pill">
                            <IcPackageOpen size={10} />
                            {getProductCountForCategory(category.id)}
                          </span>
                        </td>
                        <td className="td-ki px-5 py-3.5 text-sm whitespace-nowrap text-right">
                          <div className="flex justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleAddSubcategoryClick(category.id, category.name)}
                              className="h-9 w-9 flex items-center justify-center btn-mini transition-colors"
                              title="Ajouter une sous-catégorie"
                              aria-label="Ajouter une sous-catégorie"
                            >
                              <IcPlus />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(category)}
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors"
                              title="Modifier la catégorie"
                              aria-label="Modifier la catégorie"
                            >
                              <IcPencil />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(category)}
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                              title="Supprimer la catégorie"
                              aria-label="Supprimer la catégorie"
                            >
                              <IcTrash />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Sous-catégories : lignes gris clair, nom en retrait avec
                          un filet vertical (plus de « └─ » ASCII). */}
                      {open && subs.length === 0 && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td colSpan={4} className="td-ki px-5 py-2.5 text-xs text-gray-400">
                            <span className="ml-9 pl-3 border-l-2 border-gray-200">Aucune sous-catégorie</span>
                          </td>
                        </tr>
                      )}
                      {open && subs.map((subcategory) => {
                        const subProductCount = getProductCountForSubcategory(subcategory.id);
                        return (
                          <tr key={`sub-${subcategory.id}`} className="border-t border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
                            <td className="td-ki px-5 py-2.5 text-sm">
                              <div className="ml-9 pl-3 border-l-2 border-gray-200 min-w-0">
                                <p className="text-gray-700 leading-tight">{subcategory.name}</p>
                                {subcategory.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 max-w-md truncate" title={subcategory.description}>
                                    {subcategory.description}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="td-ki px-5 py-2.5 text-sm text-center text-gray-300">—</td>
                            <td className="td-ki px-5 py-2.5 text-sm whitespace-nowrap text-center">
                              <span className="bdg bdg-pill">
                                <IcPackageOpen size={10} />
                                {subProductCount}
                              </span>
                            </td>
                            <td className="td-ki px-5 py-2.5 text-sm whitespace-nowrap text-right">
                              <div className="flex justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleEditSubcategory(subcategory)}
                                  className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors"
                                  title="Modifier la sous-catégorie"
                                  aria-label="Modifier la sous-catégorie"
                                >
                                  <IcPencil />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSubcategory(subcategory)}
                                  className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                  title="Supprimer la sous-catégorie"
                                  aria-label="Supprimer la sous-catégorie"
                                >
                                  <IcTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );

  // Paramètres (owner 2026-09-09, « plus simple, sans scroll ») : 2 volets —
  // à gauche la liste des catégories (nom + nb de sous-catégories), à droite
  // la catégorie choisie : ses sous-catégories en tableau court + ses actions.
  const selectedCategory = categories.find((c) => c.id === selectedCatId) || categories[0] || null;
  const selectedSubs = selectedCategory ? subcategories.filter((sub) => sub.category_id === selectedCategory.id) : [];
  const paneContent = loading ? (
    <div className="flex justify-center items-center h-64">
      <CardSkeleton />
    </div>
  ) : categories.length === 0 ? (
    <div className="empty-ki rounded-2xl p-12 m-6">
      <IcTags size={48} className="empty-ki-icon w-12 h-12 mb-4" />
      <p className="empty-ki-title text-lg mt-0">Aucune catégorie</p>
      <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">Pour commencer, créez votre première catégorie de produits.</p>
      <button type="button" onClick={handleNewCategory} className="btn-add-block w-auto mt-4 px-4 py-2.5">
        <IcPlus className="mr-2" /> Ajouter une catégorie
      </button>
    </div>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr]">
      {/* Volet gauche : catégories (ligne active = fond gris, nom 600) */}
      <nav className="border-b md:border-b-0 md:border-r border-gray-100 p-2 flex gap-1 overflow-x-auto md:block md:overflow-visible" aria-label="Catégories">
        {categories.map((category) => {
          const active = selectedCategory && category.id === selectedCategory.id;
          const n = getSubcategoryCountForCategory(category.id);
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setSelectedCatId(category.id)}
              aria-current={active ? 'true' : undefined}
              className={`shrink-0 md:w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm text-left whitespace-nowrap transition-colors ${
                active ? 'bg-gray-100 text-gray-900 font-semibold' : 'text-gray-700 hover:bg-gray-50 font-medium'
              }`}
            >
              <span className="truncate">{category.name}</span>
              <span className={`text-xs tabular-nums ${active ? 'text-gray-500' : 'text-gray-400'}`}>{n}</span>
            </button>
          );
        })}
      </nav>

      {/* Volet droit : catégorie choisie */}
      {selectedCategory && (
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">{selectedCategory.name}</h3>
              <p className="text-xs text-gray-400 mt-0.5 tabular-nums">
                {selectedSubs.length} sous-catégorie(s) · {getProductCountForCategory(selectedCategory.id)} produit(s)
                {selectedCategory.description ? ` · ${selectedCategory.description}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button type="button" onClick={() => handleEdit(selectedCategory)} className="btn-g-edit gap-2 px-3 py-1.5 text-sm transition-colors">
                <IcPencil /> Modifier
              </button>
              <button type="button" onClick={() => handleDeleteCategory(selectedCategory)} className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Supprimer la catégorie" aria-label="Supprimer la catégorie">
                <IcTrash />
              </button>
            </div>
          </div>

          {selectedSubs.length === 0 ? (
            <p className="px-6 py-8 text-sm text-gray-400 text-center">Aucune sous-catégorie dans « {selectedCategory.name} ».</p>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr>
                  <th scope="col" className="th-ki px-6 py-2.5 text-left uppercase">Sous-catégorie</th>
                  <th scope="col" className="th-ki px-6 py-2.5 text-center uppercase w-28">Produits</th>
                  <th scope="col" className="th-ki px-6 py-2.5 text-right uppercase w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedSubs.map((sub) => (
                  <tr key={sub.id} className="border-t border-gray-100 hover:bg-gray-100 transition-colors">
                    <td className="td-ki px-6 py-2.5 text-sm">
                      <p className="text-gray-900 font-medium leading-tight">{sub.name}</p>
                      {sub.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md" title={sub.description}>{sub.description}</p>}
                    </td>
                    <td className="td-ki px-6 py-2.5 text-sm text-center">
                      <span className="bdg bdg-pill"><IcPackageOpen size={10} />{getProductCountForSubcategory(sub.id)}</span>
                    </td>
                    <td className="td-ki px-6 py-2.5 text-sm text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => handleEditSubcategory(sub)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors" title="Modifier la sous-catégorie" aria-label="Modifier la sous-catégorie">
                          <IcPencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDeleteSubcategory(sub)} className="h-8 w-8 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Supprimer la sous-catégorie" aria-label="Supprimer la sous-catégorie">
                          <IcTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* « Ajouter » pleine largeur (btn-add-block) sous la liste */}
          <div className="px-6 py-4 border-t border-gray-100">
            <button type="button" onClick={() => handleAddSubcategoryClick(selectedCategory.id, selectedCategory.name)} className="btn-add-block px-4 py-2.5 text-sm">
              <IcPlus className="text-xs" /> Ajouter une sous-catégorie
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
      {/* En-tête : chip gris (page seule) ou titre de section (Paramètres),
          « Ajouter une catégorie » btn-add-block à droite. */}
      {/* Paramètres (Studio owner 2026-09-09, même règle que Services) : ni titre,
          ni recherche, ni cartes KPI — le tableau seul, « Ajouter une catégorie »
          dessous à droite. La page /categories seule garde tout. */}
      {!embedded && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm self-start">
          <IcTags size={13} /> Gestion des catégories
        </button>
        <button type="button" onClick={handleNewCategory} className="btn-add-block w-auto px-4 py-2.5 transition-colors self-start sm:self-auto">
          <IcPlus className="mr-2" />
          Ajouter une catégorie
        </button>
      </div>
      )}

      {/* Barre de recherche pleine largeur — même toolbar que /clients et /products. */}
      {!embedded && (
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          className="inp h-12 w-full pl-11 pr-10"
          placeholder="Rechercher une catégorie ou une sous-catégorie..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => setSearch('')}
            aria-label="Effacer la recherche"
          >
            <IcClose />
          </button>
        )}
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Cartes KPI (Composants-2 §5) — informatives, même habillage que /clients (page seule). */}
      {!embedded && !loading && categories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="kpi rounded-2xl p-5 text-left">
            <IcLayers size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Catégories</p>
            <p className="kpi-value text-2xl">{categories.length}</p>
            <p className="kpi-sub text-xs mt-1">Rayons du catalogue</p>
          </div>
          <div className="kpi rounded-2xl p-5 text-left">
            <IcNetwork size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Sous-catégories</p>
            <p className="kpi-value text-2xl">{subcategories.length}</p>
            <p className="kpi-sub text-xs mt-1">Réparties dans les catégories</p>
          </div>
          <div className="kpi rounded-2xl p-5 text-left">
            <IcPackageOpen size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Produits classés</p>
            <p className="kpi-value text-2xl">{classifiedProducts}</p>
            <p className="kpi-sub text-xs mt-1">
              {products.length - classifiedProducts > 0
                ? `${products.length - classifiedProducts} produit(s) sans catégorie`
                : 'Tous les produits ont une catégorie'}
            </p>
          </div>
        </div>
      )}

      {/* Paramètres : même cadre que Tarification (owner 2026-09-09) — « Tout
          déplier » dans l'entête, tableau bord à bord, « Ajouter une catégorie » en pied. */}
      {embedded ? (
        <SectionCard
          title="Catégories produits"
          sub={`${categories.length} catégorie(s) · ${subcategories.length} sous-catégorie(s)`}
          flush
          action={!loading && (
            <button
              type="button"
              onClick={handleNewCategory}
              className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
              title="Ajouter une catégorie"
              aria-label="Ajouter une catégorie"
            >
              <IcPlus />
            </button>
          )}
        >
          {paneContent}
        </SectionCard>
      ) : listContent}

      {/* Modale catégorie — même patron que la modale sous-catégorie
          (Brand Kit §05 : entête encre, corps 24px, pied filet gray-100). */}
      {showCategoryForm && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={(e) => {
          if (e.target === e.currentTarget) handleCancel();
        }}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h3>
              <button type="button" onClick={handleCancel} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor="name">
                  Nom <span className="req">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900"
                  placeholder="Nom de la catégorie"
                  autoFocus
                  required
                />
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900"
                  placeholder="Description de la catégorie"
                ></textarea>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                <button type="button" onClick={handleCancel} className="px-4 py-2.5 btn-g transition-colors">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2.5 flex items-center btn-ink transition-colors">
                  {isEditing ? <IcPencil className="mr-2" /> : <IcPlus className="mr-2" />}
                  {isEditing ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal Form */}
      {showSubcategoryForm && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={(e) => {
          if (e.target === e.currentTarget) handleCancelSubcategory();
        }}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {isEditingSubcategory ? 'Modifier la sous-catégorie' : 'Ajouter une sous-catégorie'}
              </h3>
              <button
                onClick={handleCancelSubcategory}
                className="text-gray-400 hover:text-white"
              >
                <IcClose size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitSubcategory} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor="subcategory_name">
                  Nom <span className="req">*</span>
                </label>
                <input
                  type="text"
                  id="subcategory_name"
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryChange}
                  className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900"
                  placeholder="Nom de la sous-catégorie"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor="subcategory_category">
                  Catégorie parent <span className="req">*</span>
                </label>
                <select
                  id="subcategory_category"
                  name="category_id"
                  value={subcategoryFormData.category_id || ''}
                  onChange={handleSubcategoryChange}
                  className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                  required
                  disabled={isEditingSubcategory}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor="subcategory_description">
                  Description
                </label>
                <textarea
                  id="subcategory_description"
                  name="description"
                  value={subcategoryFormData.description}
                  onChange={handleSubcategoryChange}
                  rows="3"
                  className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900"
                  placeholder="Description de la sous-catégorie"
                ></textarea>
              </div>

              {/* Pied de modale (Brand Kit §05) : 11/16px, filet gray-100, gap 8 */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                <button
                  type="button"
                  onClick={handleCancelSubcategory}
                  className="px-4 py-2.5 btn-g transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 flex items-center btn-ink transition-colors"
                >
                  {isEditingSubcategory ? <IcPencil className="mr-2" /> : <IcPlus className="mr-2" />}
                  {isEditingSubcategory ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
